import React, { useMemo } from 'react'
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	useReactTable,
	getPaginationRowModel,
	PaginationState,
} from '@tanstack/react-table'
import { startOfMonth } from 'date-fns'
import { ChannelData } from '../../types'
import { Skeleton } from '@/common/ui/skeleton'
import { Button } from '@/common/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { ScrollArea, ScrollBar } from '@/common/ui/scroll-area'
import {
	DoubleArrowLeftIcon,
	DoubleArrowRightIcon,
} from '@radix-ui/react-icons'
import { ChevronLeftIcon, ChevronRightIcon, Eye } from 'lucide-react'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { UpdateChannelFormModal } from '@/modules/manage-player/components/GetChannel/GetChannelTable/CellAction/components/UpdateChannelFormModal'
import { DashboardKeys } from '@/modules/dashboard/keys'
import { useQueryClient } from '@tanstack/react-query'

interface Props {
	data: ChannelData[]
	total: number
	isLoading?: boolean
	pageSizeOptions?: number[]
}

const columnHelper = createColumnHelper<ChannelData>()

declare module '@tanstack/react-table' {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	interface ColumnMeta<TData, TValue> {
		isFirstInGroup?: boolean
		style?: React.CSSProperties
	}
}

const ChannelAnalyticTable: React.FC<Props> = ({
	data,
	total,
	isLoading,
	pageSizeOptions = [10, 20, 30, 40, 50],
}) => {
	const navigate = useNavigate()
	const { pageSize, setPageSize, currentPage, setCurrentPage } =
		useTablePagination()

	const paginationState = {
		pageIndex: currentPage - 1, // zero-based index for React Table
		pageSize: pageSize,
	}

	const queryClient = useQueryClient()

	const pageCount = Math.ceil(total / pageSize)

	const handlePaginationChange = (
		updaterOrValue:
			| PaginationState
			| ((old: PaginationState) => PaginationState),
	) => {
		const pagination =
			typeof updaterOrValue === 'function'
				? updaterOrValue(paginationState)
				: updaterOrValue

		setCurrentPage(pagination.pageIndex + 1) // converting zero-based index to one-based
		setPageSize(pagination.pageSize)
	}

	const columns = useMemo(() => {
		const baseColumns = [
			columnHelper.accessor('name', {
				header: 'ช่องทาง',
				cell: (info) => info.getValue(),
				meta: {
					style: {
						position: 'sticky',
						left: 0,
						zIndex: 1,
						minWidth: '180px',
					},
				},
			}),
			columnHelper.accessor('isChannelActive', {
				header: 'สถานะ',
				cell: (info) => (
					<div className="flex items-center justify-center">
						<span
							className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
								info.getValue()
									? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200'
									: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200'
							}`}
						>
							{info.getValue() ? 'มีการใช้งาน' : 'ไม่มีการใช้งาน'}
						</span>
					</div>
				),
				meta: {
					style: {
						position: 'sticky',
						left: '180px',
						zIndex: 1,
						minWidth: '120px',
					},
				},
			}),
			columnHelper.accessor('note', {
				header: 'หมายเหตุ',
				cell: (info) => {
					const note = info.getValue()
					if (!note) return '-'

					return (
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button variant="ghost" size="sm" className="h-6 w-6 p-0">
										<Eye className="h-4 w-4" />
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									<p className="max-w-xs break-words">{note}</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					)
				},
				meta: {
					style: {
						position: 'sticky',
						left: '300px',
						zIndex: 1,
						minWidth: '80px',
					},
				},
			}),
			columnHelper.accessor('activateAt', {
				header: 'สมัครครั้งแรก',
				cell: (info) => {
					const value = info.getValue()
					if (!value) return '-'
					const date = new Date(value)
					if (isNaN(date.getTime())) return '-'
					return format(date, 'PPP', { locale: th })
				},
				meta: {
					style: {
						position: 'sticky',
						left: '380px',
						zIndex: 1,
						minWidth: '180px',
					},
				},
			}),
			columnHelper.accessor('cost', {
				id: 'cost',
				header: 'ต้นทุน',
				cell: (info) => (
					<div className="flex items-center justify-end space-x-2 text-left">
						<span>
							{new Intl.NumberFormat('th-TH', {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							}).format(info.getValue())}
						</span>
						<UpdateChannelFormModal
							data={{
								id: info.row.original.id,
								name: info.row.original.name,
								cost: info.getValue(),
								note: info.row.original.note,
							}}
							onSuccess={() => {
								queryClient.refetchQueries({
									queryKey: [DashboardKeys.ChaneelAnalytics],
								})
							}}
						/>
					</div>
				),
				meta: {
					style: {
						position: 'sticky',
						left: '560px',
						zIndex: 1,
						minWidth: '150px',
					},
				},
			}),
		]

		const dayColumns = [-1, 1, 3, 7, 30, 60, 90, 120]

		const analyticsColumns = dayColumns.map((day) => {
			const baseColumns = [
				{
					id: `totalPlayerCount${day}`,
					header: 'ผู้เล่น',
					cell: ({ row }: { row: any }) => {
						const analytic = row.original.analytics.find(
							(a: any) => a.dayAfterActivation === day,
						)
						return (
							<span className="text-red-500 dark:text-red-400">
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalPlayerCount || 0,
								)}
							</span>
						)
					},
					meta: { isFirstInGroup: true },
				},
				{
					id: `totalPlayerActivateCount${day}`,
					header: 'ผู้เล่นที่ใช้งาน',
					cell: ({ row }: { row: any }) => {
						const analytic = row.original.analytics.find(
							(a: any) => a.dayAfterActivation === day,
						)
						return (
							<span className="text-green-500 dark:text-green-400">
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalActivePlayerCount || 0,
								)}
							</span>
						)
					},
				},
				{
					id: `costPerUser${day}`,
					header: 'ต้นทุนต่อผู้เล่น / ต่อผู้เล่นที่ใช้งาน',
					cell: ({ row }: { row: any }) => {
						const analytic = row.original.analytics.find(
							(a: any) => a.dayAfterActivation === day,
						)
						return (
							<>
								<span className="text-red-500 dark:text-red-400">
									{new Intl.NumberFormat('th-TH').format(
										analytic?.costPerPlayer || 0,
									)}
								</span>
								{' / '}
								<span className="text-green-500 dark:text-green-400">
									{new Intl.NumberFormat('th-TH').format(
										analytic?.costPerActivePlayer || 0,
									)}
								</span>
							</>
						)
					},
				},
				{
					id: `totalTransactionTime${day}`,
					header: 'จำนวนฝาก/ถอน',
					cell: ({ row }: { row: any }) => {
						const analytic = row.original.analytics.find(
							(a: any) => a.dayAfterActivation === day,
						)
						return (
							<>
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalDepositTime || 0,
								)}
								{' / '}
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalWithdrawTime || 0,
								)}
							</>
						)
					},
				},
				{
					id: `totalDepositAmount${day}`,
					header: 'รวมฝาก/ถอน',
					cell: ({ row }: { row: any }) => {
						const analytic = row.original.analytics.find(
							(a: any) => a.dayAfterActivation === day,
						)
						return (
							<>
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalDepositAmount || 0,
								)}
								{' / '}
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalWithdrawAmount || 0,
								)}
							</>
						)
					},
				},
				{
					id: `totalProfit${day}`,
					header: 'กำไร',
					cell: ({ row }: { row: any }) => {
						const analytic = row.original.analytics.find(
							(a: any) => a.dayAfterActivation === day,
						)
						return (
							<span
								className={`${(analytic?.totalProfit || 0) >= 0 ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}
							>
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalProfit || 0,
								)}
							</span>
						)
					},
				},
				{
					id: `totalDepositAmountAvg${day}`,
					header: 'เฉลี่ยจำนวนฝาก/ถอน',
					cell: ({ row }: { row: any }) => {
						const analytic = row.original.analytics.find(
							(a: any) => a.dayAfterActivation === day,
						)
						return (
							<>
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalDepositAmountAvg || 0,
								)}
								{' / '}
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalWithdrawAmountAvg || 0,
								)}
							</>
						)
					},
				},
				{
					id: `totalDepositTimeAvg${day}`,
					header: 'เฉลี่ยจำนวนฝาก/ถอน',
					cell: ({ row }: { row: any }) => {
						const analytic = row.original.analytics.find(
							(a: any) => a.dayAfterActivation === day,
						)
						return (
							<>
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalDepositTimeAvg || 0,
								)}
								{' / '}
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalWithdrawTimeAvg || 0,
								)}
							</>
						)
					},
				},
				{
					id: `totalProfitAvg${day}`,
					header: 'เฉลี่ยกำไร',
					cell: ({ row }: { row: any }) => {
						const analytic = row.original.analytics.find(
							(a: any) => a.dayAfterActivation === day,
						)
						return (
							<span
								className={`${(analytic?.totalProfitAvg || 0) >= 0 ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}
							>
								{new Intl.NumberFormat('th-TH').format(
									analytic?.totalProfitAvg || 0,
								)}
							</span>
						)
					},
				},
			]

			// Add costMinusProfit column only for day -1
			if (day === -1) {
				baseColumns.push({
					id: 'costMinusProfit',
					header: 'กำไร - ต้นทุน',
					cell: ({ row }: { row: any }) => {
						const analytic = row.original.analytics.find(
							(a: any) => a.dayAfterActivation === day,
						)

						return (
							<span
								className={`${(analytic?.costMinusProfit || 0) >= 0 ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}
							>
								{new Intl.NumberFormat('th-TH').format(
									analytic?.costMinusProfit || 0,
								)}
							</span>
						)
					},
				})
			}

			return columnHelper.group({
				id: `day${day}`,
				header: () => (
					<div className="text-center">{`${day === -1 ? 'สะสม' : `${day} วัน`}`}</div>
				),
				columns: baseColumns,
			})
		})

		return [...baseColumns, ...analyticsColumns]
	}, [])

	const table = useReactTable({
		data,
		columns,
		pageCount: pageCount,
		state: {
			pagination: paginationState,
		},
		onPaginationChange: handlePaginationChange,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		manualPagination: true,
		manualFiltering: true,
	})

	if (isLoading) {
		return (
			<div className="space-y-3">
				<Skeleton className="h-10 w-full" />
				<Skeleton className="h-10 w-full" />
				<Skeleton className="h-10 w-full" />
			</div>
		)
	}

	if (!data.length) {
		return (
			<div className="flex h-40 items-center justify-center rounded-md border bg-card">
				<p className="text-muted-foreground">ไม่พบข้อมูล</p>
			</div>
		)
	}

	return (
		<div className="space-y-4">
			<ScrollArea className="h-[calc(80dvh-220px)] rounded-md border md:h-[calc(90dvh-240px)]">
				<table className="min-w-full divide-y divide-border">
					<thead className="bg-muted">
						{table.getHeaderGroups().map((headerGroup) => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<th
										key={header.id}
										colSpan={header.colSpan}
										style={{
											minWidth: header.id === 'name' ? '150px' : '100px',
											...(header.column.columnDef.meta?.style || {}),
										}}
										className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground ${
											header.column.columnDef.meta?.isFirstInGroup
												? 'border-l border-border'
												: ''
										} ${header.column.columnDef.meta?.style?.position === 'sticky' ? 'bg-muted' : ''}`}
									>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody className="divide-y divide-border bg-card">
						{table.getRowModel().rows.map((row) => (
							<tr key={row.id} className="hover:bg-muted/50">
								{row.getVisibleCells().map((cell) => (
									<td
										key={cell.id}
										style={{
											minWidth: cell.column.id === 'name' ? '150px' : '100px',
											...(cell.column.columnDef.meta?.style || {}),
										}}
										className={`whitespace-nowrap px-6 py-4 text-sm text-card-foreground ${
											cell.column.columnDef.meta?.isFirstInGroup
												? 'border-l border-border'
												: ''
										} ${cell.column.id !== 'cost' ? 'cursor-pointer' : ''} ${cell.column.columnDef.meta?.style?.position === 'sticky' ? 'bg-card' : ''}`}
										onClick={() => {
											if (cell.column.id !== 'cost') {
												const today = new Date()
												const startDate = format(
													startOfMonth(today),
													'yyyy-MM-dd',
												)
												const endDate = format(today, 'yyyy-MM-dd')
												navigate(
													`/operator/channel-analytic/${row.original.id}?startDate=${startDate}&endDate=${endDate}&dateRange=thisMonth`,
												)
											}
										}}
									>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
				<ScrollBar orientation="horizontal" />
			</ScrollArea>

			<div className="flex flex-col items-center justify-end gap-2 space-x-2 py-4 sm:flex-row">
				<div className="flex w-full items-center justify-between">
					<div className="flex-1 text-sm text-muted-foreground">
						{total > 0 ? (
							<>
								แสดง {paginationState.pageIndex * paginationState.pageSize + 1}{' '}
								ถึง{' '}
								{Math.min(
									(paginationState.pageIndex + 1) * paginationState.pageSize,
									total,
								)}{' '}
								จาก {total} รายการ
							</>
						) : (
							'ไม่พบข้อมูล'
						)}
					</div>
					<div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
						<div className="flex items-center space-x-2">
							<p className="whitespace-nowrap text-sm font-medium">
								จำนวนแถวต่อหน้า
							</p>
							<Select
								value={`${paginationState.pageSize}`}
								onValueChange={(value) => {
									table.setPageSize(Number(value))
								}}
							>
								<SelectTrigger className="h-8 w-[70px]">
									<SelectValue placeholder={paginationState.pageSize} />
								</SelectTrigger>
								<SelectContent side="top">
									{pageSizeOptions.map((pageSize) => (
										<SelectItem key={pageSize} value={`${pageSize}`}>
											{pageSize}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</div>
				<div className="flex w-full items-center justify-between gap-2 sm:justify-end">
					<div className="flex w-[150px] items-center justify-center text-sm font-medium">
						{total > 0 ? (
							<>
								หน้า {paginationState.pageIndex + 1} จาก {pageCount}
							</>
						) : (
							'ไม่มีหน้า'
						)}
					</div>
					<div className="flex items-center space-x-2">
						<Button
							aria-label="ไปหน้าแรก"
							variant="outline"
							className="hidden h-8 w-8 p-0 lg:flex"
							onClick={() => table.setPageIndex(0)}
							disabled={!table.getCanPreviousPage() || isLoading}
						>
							<DoubleArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="ไปหน้าก่อนหน้า"
							variant="outline"
							className="h-8 w-8 p-0"
							onClick={() => table.previousPage()}
							disabled={!table.getCanPreviousPage() || isLoading}
						>
							<ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="ไปหน้าถัดไป"
							variant="outline"
							className="h-8 w-8 p-0"
							onClick={() => table.nextPage()}
							disabled={!table.getCanNextPage() || isLoading}
						>
							<ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
						<Button
							aria-label="ไปหน้าสุดท้าย"
							variant="outline"
							className="hidden h-8 w-8 p-0 lg:flex"
							onClick={() => table.setPageIndex(table.getPageCount() - 1)}
							disabled={!table.getCanNextPage() || isLoading}
						>
							<DoubleArrowRightIcon className="h-4 w-4" aria-hidden="true" />
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default ChannelAnalyticTable
